import React, { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import classNames from 'classnames';

import * as api from '../../../api';
import * as Device from '../../../utils/device';
import * as store from '../../../reducers/store';

import ProviderImage from '../../../components/ProviderImage';
import { InfoMessage } from '../../../components/InfoMessage';
import LoadingBar from '../../../components/LoadingBar';
import { PhoneNumberView } from '../../ConfirmAppointment/PhoneNumberView';

import { sendEventMessage, Message } from '../../../utils/native';
import { getProviderNameFriendly } from '../../../utils/provider';
import { htmlFromMarkdown } from '../../../utils/markdown';
import { isLocalBuild } from 'common/dist/utils/url';
import { useAsyncFlow } from '../../../hooks/common/useAsyncFlow';
import { getStartAndArrivalTime } from '../../../utils/dateTime';
import { InstructionType, InstructionView } from './InstructionView';
import { ReactComponent as ClockSvg } from '../../../assets/clock.svg';
import { ReactComponent as MapPinSvg } from '../../../assets/map-pin.svg';
import { ReactComponent as PhoneSvg } from '../../../assets/phone.svg';
import { SwiperWithArrows } from '../../../components/SwiperWithArrows';

import appleWalletEn from '../../../assets/add-to-apple-wallet-en.svg';
import appleWalletEs from '../../../assets/add-to-apple-wallet-es.svg';
import saveToGooglePayEn from '../../../assets/save-to-google-pay-light-en.svg';
import saveToGooglePayEs from '../../../assets/save-to-google-pay-light-es.svg';
import CalendarEvent from '../../../components/CalendarEvent';

import styles from './index.module.css';

const QUARTET_TEST_PRACTICE_ID =
  'fda427ee-e76d-4bc8-963c-6b577439e473' as const;

interface AddressProps {
  practice: api.Practice.Practice;
}
interface SharedProps {
  formSetId: string;
  preferredLanguage?: store.Language;
}

interface CalendarProps {
  encounter: api.Encounter.Encounter;
  provider: api.User.User;
  practice?: api.Practice.Practice;
}

interface CalendarAndPaymentProps extends CalendarProps, SharedProps {}

interface Props {
  infoMessage?: React.ReactNode;
  practicePreference: store.All['masterPracticePreference'];
  label: string;
}

const LastPage: React.FC<Props> = ({
  infoMessage,
  practicePreference: {
    enableOutreachIntakes,
    enablePrevisitPassbookAndPayPass,
    showArrivalTimeOnly,
    hideEncounterStartTimeByVisitKind,
    frontDeskCheckInEnabled,
  },
  label,
}) => {
  const { t } = useTranslation();
  const preferredLanguage = useSelector(
    (state: store.Loaded) => state.preferredLanguage,
  );
  const formSets = useSelector(
    (state: store.Loaded) => Object.values(state.formSets),
    shallowEqual,
  );
  const [baseFormSet] = formSets;
  const {
    error: mobileCheckInError,
    pending,
    execute: mobileCheckIn,
  } = useAsyncFlow(async () => {
    await api.client.Previsit.checkIn({
      previsitFormSetId: baseFormSet.id,
    });
    sendEventMessage(Message.FormCompleted);
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const isFrontDeskExperience =
      navigator.userAgent.match(/com.vivaa.FrontDesk/);

    if (frontDeskCheckInEnabled && !!isFrontDeskExperience) {
      mobileCheckIn().then(({ error }) => {
        if (error) {
          timeoutId = setTimeout(() => {
            sendEventMessage(Message.Logout);
          }, 10000);
        }
      });
    } else {
      sendEventMessage(Message.FormCompleted);
    }

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontDeskCheckInEnabled]);

  if (pending) {
    return <LoadingBar label={t('processingCheckin')} />;
  }

  if (mobileCheckInError) {
    return <InfoMessage isError>{t('errorProcessingCheckIn')}</InfoMessage>;
  }

  return (
    <div className={styles.LastPage}>
      <div className={styles.Title}>
        <div
          className={styles.Label}
          dangerouslySetInnerHTML={{
            __html: htmlFromMarkdown(label),
          }}
        ></div>
      </div>
      {infoMessage && (
        <div className={styles.InfoMessage}>
          <InfoMessage>{infoMessage}</InfoMessage>
        </div>
      )}
      {formSets.length === 1 &&
        baseFormSet.encounter &&
        baseFormSet.provider && (
          <EndCardView
            formSetId={baseFormSet.id}
            encounter={baseFormSet.encounter}
            provider={baseFormSet.provider}
            practice={baseFormSet.practice ?? undefined}
            enableOutreachIntakes={enableOutreachIntakes}
            enablePrevisitPassbookAndPayPass={enablePrevisitPassbookAndPayPass}
            virtualVisit={baseFormSet.virtualVisit}
            covidInstruction={baseFormSet.covidInstruction}
            preferredLanguage={preferredLanguage}
            showArrivalTimeOnly={showArrivalTimeOnly}
            hideEncounterStartTimeByVisitKind={
              hideEncounterStartTimeByVisitKind
            }
          />
        )}
      {formSets.length > 1 && (
        <SwiperWithArrows
          totalSize={formSets.length}
          formatLabel={(currentIndex, totalSize) =>
            `Visit ${currentIndex + 1} of ${totalSize}`
          }
          className={styles.SlideContainer}
        >
          {formSets
            .filter(
              (formSet) =>
                formSet.encounter && formSet.provider && formSet.practice,
            )
            .map(
              ({
                id,
                encounter,
                provider,
                practice,
                virtualVisit,
                covidInstruction,
              }) => {
                return (
                  <SwiperSlide key={`slide-${id}`}>
                    <EndCardView
                      key={`card-view-${id}`}
                      formSetId={id}
                      encounter={encounter!}
                      provider={provider!}
                      practice={practice!}
                      enableOutreachIntakes={enableOutreachIntakes}
                      enablePrevisitPassbookAndPayPass={
                        enablePrevisitPassbookAndPayPass
                      }
                      virtualVisit={virtualVisit}
                      covidInstruction={covidInstruction}
                      preferredLanguage={preferredLanguage}
                      showArrivalTimeOnly={showArrivalTimeOnly}
                      hideEncounterStartTimeByVisitKind={
                        hideEncounterStartTimeByVisitKind
                      }
                    />
                  </SwiperSlide>
                );
              },
            )}
        </SwiperWithArrows>
      )}
    </div>
  );
};

const EndCardView: React.FC<{
  formSetId: string;
  encounter: api.Encounter.Encounter;
  provider: api.User.User;
  preferredLanguage: api.Language.Language;
  practice?: api.Practice.Practice;
  enableOutreachIntakes: boolean;
  enablePrevisitPassbookAndPayPass: boolean;
  virtualVisit?: boolean;
  covidInstruction?: string;
  showArrivalTimeOnly: boolean;
  hideEncounterStartTimeByVisitKind: string[];
}> = ({
  formSetId,
  encounter,
  provider,
  preferredLanguage,
  practice,
  enableOutreachIntakes,
  enablePrevisitPassbookAndPayPass,
  showArrivalTimeOnly,
  virtualVisit,
  covidInstruction,
  hideEncounterStartTimeByVisitKind,
}) => {
  const { t } = useTranslation();
  const hideTimestamp = encounter?.kindOfVisit
    ? hideEncounterStartTimeByVisitKind.includes(encounter.kindOfVisit)
    : false;

  if (hideEndCardView(practice)) return null;
  return (
    <div className={styles.ProviderCard}>
      <div className={styles.ProviderDetails}>
        <ProviderImage
          className={styles.ProviderImage}
          provider={provider}
          size="Small"
        />

        <div className={styles.ProviderText}>
          <div className={styles.ProviderName}>
            {getProviderNameFriendly(provider, [
              'firstName',
              'lastName',
              'suffix',
            ])}
          </div>
          {encounter.specialty &&
            encounter.specialty !== api.Encounter.Specialty.Other && (
              <div className={styles.ProviderSpecialty}>
                {encounter.specialty
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map(_.toLower)
                  .map(_.upperFirst)
                  .join(' ')}
              </div>
            )}
        </div>
      </div>

      <div className={styles.AppointmentDetailsList}>
        {!enableOutreachIntakes && isVisitEncounter(encounter) && (
          <div className={styles.AppointmentDetailRow}>
            <ClockSvg className={styles.IconItem} />
            <div
              className={classNames(
                styles.AppointmentTime,
                styles.AppointmentDetailContent,
              )}
            >
              {getStartAndArrivalTime({
                startTime: encounter.startTime,
                arrivalTime: encounter.arrivalTime,
                timezone: practice?.timezone,
                showArrivalTimeOnly,
                hideTimestamp,
              })}
            </div>
          </div>
        )}

        {(practice?.address || practice?.city || practice?.state) && (
          <div className={styles.AppointmentDetailRow}>
            <MapPinSvg className={styles.IconItem} />
            {virtualVisit ? (
              <div
                className={classNames(
                  styles.FormattedAddress,
                  styles.LocationName,
                  styles.AppointmentDetailContent,
                )}
              >
                {t('Virtual Visit')}
              </div>
            ) : (
              <FormattedAddress practice={practice} />
            )}
          </div>
        )}
        {practice?.phoneNumber && (
          <div className={styles.AppointmentDetailRow}>
            <PhoneSvg className={styles.IconItem} />{' '}
            <PhoneNumberView
              className={classNames(
                styles.PhoneNumber,
                styles.AppointmentDetailContent,
              )}
              phoneNumber={practice?.phoneNumber}
            />
          </div>
        )}
        {encounter.patientDirections && (
          <InstructionView
            instructionType={InstructionType.Directions}
            patientInstruction={encounter.patientDirections}
          />
        )}
        {(encounter.patientInstructions || covidInstruction) && (
          <InstructionView
            instructionType={InstructionType.Instructions}
            patientInstruction={encounter.patientInstructions}
            covidInstruction={covidInstruction}
          />
        )}
      </div>

      {!enableOutreachIntakes &&
        isVisitEncounter(encounter) &&
        enablePrevisitPassbookAndPayPass && (
          <PaymentAndCalendarLinks
            encounter={encounter}
            provider={provider}
            practice={practice}
            formSetId={formSetId}
            preferredLanguage={preferredLanguage}
          />
        )}
    </div>
  );
};

function isVisitEncounter(encounter: api.Encounter.Encounter): boolean {
  return !encounter.scenarioPostVisitId;
}

function addressJoined(practice: api.Practice.Practice): string {
  return [practice.address, practice.city, practice.state]
    .filter((x) => !!x)
    .join(', ');
}

export const FormattedAddress: React.FC<AddressProps> = ({ practice }) => {
  return (
    <div
      className={classNames(
        styles.FormattedAddress,
        styles.AppointmentDetailContent,
      )}
    >
      {practice.name !== null && (
        <>
          <div className={styles.LocationName}>{practice.name}</div>
        </>
      )}
      <div className={styles.AddressLink}>{addressJoined(practice)}</div>
    </div>
  );
};

const PaymentAndCalendarLinks: React.FC<CalendarAndPaymentProps> = (props) => {
  if (isLocalBuild()) {
    return (
      <>
        <div className={styles.HorizontalButtons}>
          <AppleWallet {...props} />
          <AddToCalendarButton {...props} />
        </div>
        <GooglePay {...props} />
      </>
    );
  }
  if (Device.isIOS() || Device.isMacOS()) {
    return (
      <div className={styles.HorizontalButtons}>
        <AppleWallet {...props} />
        <AddToCalendarButton {...props} />
      </div>
    );
  }
  return (
    <>
      <GooglePay {...props} />
      <AddToCalendarButton {...props} />
    </>
  );
};

export const PaymentLinks: React.FC<SharedProps> = (props) => {
  // Used on Schedule confirmation screen
  if (isLocalBuild()) {
    return (
      <>
        <AppleWallet {...props} />
        <GooglePay {...props} />
      </>
    );
  } else if (Device.isIOS() || Device.isMacOS()) {
    return <AppleWallet {...props} />;
  } else if (Device.isAndroid()) {
    return <GooglePay {...props} />;
  } else {
    return null;
  }
};

export const AppleWallet: React.FC<SharedProps> = ({
  formSetId,
  preferredLanguage,
}) => {
  const passUrl = api.client.Previsit.getPassbookUrl(formSetId);
  const src =
    preferredLanguage === api.Language.Language.Es
      ? appleWalletEs
      : appleWalletEn;
  return (
    <a
      className={styles.AppleWallet}
      href={passUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src={src} alt="add to apple wallet" />
    </a>
  );
};

export const GooglePay: React.FC<SharedProps> = ({
  formSetId,
  preferredLanguage,
}) => {
  const paypassUrl = api.client.Previsit.getPayPassUrl(formSetId);
  const src =
    preferredLanguage === api.Language.Language.Es
      ? saveToGooglePayEs
      : saveToGooglePayEn;
  return (
    <a
      className={styles.GooglePay}
      href={paypassUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src={src} alt="add to google pay" />
    </a>
  );
};

const AddToCalendarButton: React.FC<CalendarProps> = ({
  encounter,
  provider,
  practice,
}) => {
  return (
    <CalendarEvent
      subject={`Appointment with ${getProviderNameFriendly(provider, [
        'firstName',
        'lastName',
        'suffix',
      ])}`}
      encounter={encounter}
      provider={provider}
      // Practice should exist at this point anyway
      practice={
        practice ?? {
          name: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          timezone: '',
        }
      }
    />
  );
};

const hideEndCardView = (practice?: api.Practice.Practice) => {
  return practice && practice.id === QUARTET_TEST_PRACTICE_ID;
};

export default LastPage;
